// ─── settings.js — кросс-устройственная синхронизация пользовательских настроек
//
// Архитектура: local-first. Записи в localStorage сразу видны UI, потом
// debounced-пушатся в Supabase. На login и фокусе окна — pull с сервера,
// per-key last-write-wins merge по timestamp'ам.
//
// Сервер: одна строка на пользователя в `user_settings.data` (JSONB), формат:
//   { "ac_theme": {value: "dark", ts: 1747500000000}, ... }
//
// Какие ключи синкаются: см. SYNC_KEYS / SYNC_PREFIXES ниже.
// Остальные localStorage-ключи (player position, floating window rect,
// cached server data, queues) — НЕ синкаются.

import { supabase } from './supabase';

// ── Allowlist синкаемых ключей ───────────────────────────────────────────────
const SYNC_KEYS = new Set([
  'ac_theme',               // тема light/dark/arcade
  'ac_ui_sounds',           // звуки интерфейса в «Аркаде» и «Колоде» ('0' — выкл.)
  'ac_leech_done',          // пиявки, снятые в играх (src/games/leeches.js)
  'ac_active_lang',         // выбранный язык изучения
  'ac_lang_intro_seen',     // вопрос «Какой язык учишь?» уже задавали
  'ac_daily_goal',          // дневная цель (без языка / fallback)
  'ac_sort_by',             // сортировка проектов
  'ac_openrouter_byok',     // BYOK OpenRouter ключ для AI-объяснений
  'ac_focused_mode',        // focus-режим toggle
  'ac_stats_lang',          // фильтр языка в StatsScreen
  // FSRS-4.5 настройки
  'ac_fsrs_retention',      // желаемое удержание (0.70..0.97)
  'ac_day_start_hour',      // час начала "нового дня" (0..23)
  'ac_new_limit',           // дневной лимит новых карт
  'ac_review_limit',        // дневной лимит повторений
  'ac_max_interval',        // максимальный интервал повторения, дней
  'ac_learn_steps',         // шаги заучивания новых карт («1m 10m»)
  'ac_relearn_steps',       // шаги после «Снова» в повторении («10m», пусто — без шагов)
  'ac_fsrs_daily',          // {dateKey: {newDone, reviewDone}} — Anki-style суточный счётчик
  'ac_reviewed_today',      // какие карточки сегодня уже повторяли (для «Сделано сегодня»)
  'ac_deck_order',          // порядок колод (проектов) для новых карточек
  'ac_queue',               // очередь аудио («Далее», как в подкастах)
  'ac_new_mix',             // новые: вперемешку / после / перед повторениями
  'ac_clip_len',            // плеер: длина клипа (последние N секунд)
  'ac_slow_rate',           // плеер: скорость при удержании «медленно»
  'ac_card_xl',             // язык перевода для подсказок в новой карточке
]);

const SYNC_PREFIXES = [
  'ac_daily_goal_',         // per-language daily goals (ac_daily_goal_ja, ac_daily_goal_fr)
  'ac_folders_',            // пользовательские папки библиотеки (ac_folders_{userId})
];

export function isSyncable(key) {
  if (!key || typeof key !== 'string') return false;
  if (SYNC_KEYS.has(key)) return true;
  return SYNC_PREFIXES.some(p => key.startsWith(p));
}

// ── Per-key timestamps (last-write-wins ledger) ──────────────────────────────
const TS_KEY = 'ac_settings_ts';

function _readTs() {
  try { return JSON.parse(localStorage.getItem(TS_KEY) || '{}'); } catch { return {}; }
}
function _writeTs(ts) {
  try { localStorage.setItem(TS_KEY, JSON.stringify(ts)); } catch {}
}

// ── Public: пометить настройку как локально изменённую ──────────────────────
// Вызывается ВСЕГДА после localStorage.setItem(<syncable key>, …). Обновляет
// timestamp и шедулит debounced push в Supabase.
export function markSetting(key) {
  if (!isSyncable(key)) return;
  const ts = _readTs();
  ts[key] = Date.now();
  _writeTs(ts);
  _schedulePush();
}

// Helper-обёртка: setItem + markSetting в один вызов. Удобно для новых мест,
// чтобы не забыть пометить.
export function setSyncedItem(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
    markSetting(key);
  } catch {}
}

// ── Текущий user + debounced push ────────────────────────────────────────────
let _currentUserId = null;
let _pushTimer = null;

export function setSyncUser(userId) {
  _currentUserId = userId || null;
}

function _schedulePush() {
  if (!_currentUserId) return;
  clearTimeout(_pushTimer);
  // 2 секунды — компромисс: успеваем сбатчить серию изменений (например юзер
  // быстро правит daily goal и тему подряд), но и не теряем при закрытии
  // вкладки (beforeunload hook ниже принудительно пушит).
  _pushTimer = setTimeout(() => pushSettings(_currentUserId), 2000);
}

// ── PULL: тянем с сервера, merge'им по timestamp'у ──────────────────────────
// Возвращает {applied: число изменённых ключей} либо {error}.
// Если remote.ts > local.ts → перезаписываем localStorage и шлём custom event
// 'ac:settings-updated' (компоненты слушают и перечитывают свой state).
export async function pullSettings(userId) {
  if (!userId) return { error: 'no_user' };
  try {
    const { data, error } = await supabase
      .from('user_settings')
      .select('data')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) return { error: error.message };
    const remote = (data?.data) || {};
    const localTs = _readTs();
    const changedKeys = [];

    for (const [k, entry] of Object.entries(remote)) {
      if (!isSyncable(k)) continue;
      if (!entry || typeof entry !== 'object') continue;
      const remoteTs = Number(entry.ts) || 0;
      const localTsK = Number(localTs[k]) || 0;
      if (remoteTs > localTsK) {
        try {
          if (entry.value == null) localStorage.removeItem(k);
          else localStorage.setItem(k, entry.value);
          localTs[k] = remoteTs;
          changedKeys.push(k);
        } catch {}
      }
    }
    if (changedKeys.length) {
      _writeTs(localTs);
      // Уведомляем компоненты — они перечитают свой state из localStorage.
      try {
        window.dispatchEvent(new CustomEvent('ac:settings-updated', {
          detail: { keys: changedKeys },
        }));
      } catch {}
    }
    return { applied: changedKeys.length };
  } catch (e) {
    return { error: e.message || String(e) };
  }
}

// ── PUSH: бандлим текущие локальные настройки + timestamps, отправляем ─────
export async function pushSettings(userId) {
  if (!userId) return { error: 'no_user' };
  try {
    const ts = _readTs();
    const payload = {};

    // 1. Все ключи, имеющие timestamp (значит юзер их трогал в этой сессии)
    for (const [k, t] of Object.entries(ts)) {
      if (!isSyncable(k)) continue;
      const v = localStorage.getItem(k);
      payload[k] = { value: v, ts: Number(t) || Date.now() };
    }

    // 2. Первый push (timestamps пустые) — сканируем localStorage и помечаем
    //    существующие значения timestamp'ом «сейчас». Это сохраняет уже
    //    сделанные на этом устройстве настройки для cross-device merge.
    if (Object.keys(payload).length === 0) {
      const now = Date.now();
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!isSyncable(k)) continue;
        const v = localStorage.getItem(k);
        if (v == null) continue;
        payload[k] = { value: v, ts: now };
        ts[k] = now;
      }
      _writeTs(ts);
    }

    if (Object.keys(payload).length === 0) return { pushed: 0 };

    const { error } = await supabase.from('user_settings').upsert({
      user_id: userId,
      data: payload,
    }, { onConflict: 'user_id' });
    if (error) return { error: error.message };
    return { pushed: Object.keys(payload).length };
  } catch (e) {
    return { error: e.message || String(e) };
  }
}

// ── beforeunload hook: принудительный push при закрытии вкладки ─────────────
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (!_currentUserId) return;
    if (!_pushTimer) return; // нет pending
    clearTimeout(_pushTimer);
    _pushTimer = null;
    // Synchronous push не получится (fetch async), но всё же запускаем —
    // современные браузеры дают ~secунду на in-flight запросы.
    pushSettings(_currentUserId).catch(() => {});
  });
}
